import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Text,
  Button,
  VStack,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  Alert,
  AlertIcon,
  Badge,
  Spinner,
  useColorModeValue,
  IconButton,
  Tooltip,
  Select,
  FormControl,
  FormLabel,
  FormHelperText,
  HStack,
  NumberInput,
  NumberInputField,
  Progress,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  Checkbox,
  Collapse,
} from "@chakra-ui/react";
import { 
  AddIcon, 
  EditIcon, 
  DeleteIcon, 
  CheckIcon, 
  ViewIcon, 
  InfoOutlineIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from "@chakra-ui/icons";
import axios from "axios";
import { API_URL, CURRENCY } from "../../../constants";
import { getAuthToken } from "../../../helpers/helpers";

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);
  
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [challengeStats, setChallengeStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  // New loading state for the start challenge operation
  const [isChallengeStarting, setIsChallengeStarting] = useState(false);
  
  // Active challenge evaluation (progress by condition)
  const [evaluationsById, setEvaluationsById] = useState({});
  
  // Period configuration
  const [schedulePeriodType, setSchedulePeriodType] = useState("one_week");
  const [customPeriodValue, setCustomPeriodValue] = useState("1");
  const [customPeriodUnit, setCustomPeriodUnit] = useState("weeks");
  const [currentPeriod, setCurrentPeriod] = useState(null);
  
  // New challenge form
  const [newChallengeName, setNewChallengeName] = useState("");
  const [newChallengeCondition, setNewChallengeCondition] = useState("end_date");
  const [newChallengeRevenueTarget, setNewChallengeRevenueTarget] = useState("");
  const [newChallengeProductTargets, setNewChallengeProductTargets] = useState([]);
  // Products in the current selection already tracked by other active challenges
  const [newChallengeConflicts, setNewChallengeConflicts] = useState([]);
  const [isCheckingNewConflicts, setIsCheckingNewConflicts] = useState(false);

  // Labos selection & Collapsible states
  const [selectedLabos, setSelectedLabos] = useState([]);
  const [isLaboSelectorOpen, setIsLaboSelectorOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [isInventoryLoading, setIsInventoryLoading] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");

  // Evaluate completion modal
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [isEvalLoading, setIsEvalLoading] = useState(false);
  
  // Edit challenge form
  const [editChallengeName, setEditChallengeName] = useState("");
  const [editChallengeStartDate, setEditChallengeStartDate] = useState("");
  const [editChallengeEndDate, setEditChallengeEndDate] = useState("");
  const [editChallengeProductTargets, setEditChallengeProductTargets] = useState([]);
  // Products in the current selection already tracked by other active challenges
  const [editChallengeConflicts, setEditChallengeConflicts] = useState([]);
  const [isCheckingEditConflicts, setIsCheckingEditConflicts] = useState(false);
  // Track original values for change detection
  const [originalEditValues, setOriginalEditValues] = useState({
    name: "",
    startDate: "",
    endDate: "",
    productSalesTargets: [],
  });

  const token = getAuthToken();
  const cardBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const trackBg = useColorModeValue("gray.100", "whiteAlpha.50");
  const laboBg = useColorModeValue("gray.50", "whiteAlpha.50");

  const {
    isOpen: isCreateModalOpen,
    onOpen: onCreateModalOpen,
    onClose: onCreateModalClose,
  } = useDisclosure();

  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();

  const {
    isOpen: isConflictConfirmOpen,
    onOpen: onConflictConfirmOpen,
    onClose: onConflictConfirmClose,
  } = useDisclosure();
  // Holds the pending submit action + conflicts to show while the confirmation modal is open
  const [conflictConfirmContext, setConflictConfirmContext] = useState(null);

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  const {
    isOpen: isCompleteModalOpen,
    onOpen: onCompleteModalOpen,
    onClose: onCompleteModalClose,
  } = useDisclosure();

  const {
    isOpen: isStatsModalOpen,
    onOpen: onStatsModalOpen,
    onClose: onStatsModalClose,
  } = useDisclosure();

  const {
    isOpen: isPeriodModalOpen,
    onOpen: onPeriodModalOpen,
    onClose: onPeriodModalClose,
  } = useDisclosure();

  const {
    isOpen: isEvalModalOpen,
    onOpen: onEvalModalOpen,
    onClose: onEvalModalClose,
  } = useDisclosure();

  const {
    isOpen: isConditionMetModalOpen,
    onOpen: onConditionMetModalOpen,
    onClose: onConditionMetModalClose,
  } = useDisclosure();

  const [conditionMetEvaluation, setConditionMetEvaluation] = useState(null);

  const getAuthHeaders = useCallback(() => ({
    "x-access-token": token,
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchActiveEvaluations = useCallback(async (currentChallenges) => {
    const actives = currentChallenges.filter((c) => c.isActive);
    for (const challenge of actives) {
      if (challenge.completionCondition === 'revenue_target' || challenge.completionCondition === 'product_sales_target') {
        try {
          const evalResp = await axios.get(`${API_URL}/api/challenges/${challenge._id}/evaluate`, { headers: getAuthHeaders() });
          setEvaluationsById((prev) => ({ ...prev, [challenge._id]: evalResp.data }));

          if (evalResp.data && evalResp.data.completed && challenge.isActive) {
            setConditionMetEvaluation(evalResp.data);
            setSelectedChallenge(challenge);
            onConditionMetModalOpen();
            break; 
          }
        } catch (err) {
          console.error('Error fetching active challenge evaluation:', err);
        }
      }
    }
  }, [getAuthHeaders, onConditionMetModalOpen]);

  const fetchChallenges = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/api/challenges`, {
        headers: getAuthHeaders(),
      });
      const fetchedChallenges = response.data.challenges || [];
      setChallenges(fetchedChallenges);
      fetchActiveEvaluations(fetchedChallenges);
    } catch (error) {
      console.error("Error fetching challenges:", error);
      setErrorMessage("Erreur lors du chargement des challenges.");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, fetchActiveEvaluations]);

  const fetchCurrentPeriod = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profilePointsResetSchedule`, {
        headers: getAuthHeaders(),
      });
      const period = response.data?.period;
      setCurrentPeriod(period);
      if (period) {
        applyPeriodToUi(period);
      }
    } catch (error) {
      console.error("Error fetching period:", error);
    }
  }, [getAuthHeaders]);

  const applyPeriodToUi = (period) => {
    if (!period) {
      setSchedulePeriodType("one_week");
      return;
    }

    const value = Number(period.value);
    const unit = period.unit;

    const presetKey =
      unit === "weeks" && value === 1
        ? "one_week"
        : unit === "months" && value === 1
          ? "one_month"
          : unit === "months" && value === 3
            ? "three_months"
            : unit === "months" && value === 6
              ? "six_months"
              : unit === "months" && value === 12
                ? "one_year"
                : null;

    if (presetKey) {
      setSchedulePeriodType(presetKey);
    } else {
      setSchedulePeriodType("custom");
      setCustomPeriodValue(String(value));
      setCustomPeriodUnit(unit === "months" ? "months" : "weeks");
    }
  };

  useEffect(() => {
    fetchChallenges();
    fetchCurrentPeriod();
  }, [fetchChallenges, fetchCurrentPeriod]);

  const fetchInventory = useCallback(async () => {
    try {
      setIsInventoryLoading(true);
      const response = await axios.get(`${API_URL}/api/inventory`, {
        headers: getAuthHeaders(),
      });
      const data = response.data.data || response.data;
      const productsList = Array.isArray(data) ? data : [];

      const inStockProducts = productsList.filter((p) => {
        const stockValue = p.qte_stock || 0;
        return Number(stockValue) > 0;
      });

      setInventoryProducts(inStockProducts);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setIsInventoryLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const isCreatingProductSales = newChallengeCondition === "product_sales_target" && isCreateModalOpen;
    const isEditingProductSales = isEditModalOpen && selectedChallenge?.completionCondition === "product_sales_target";
    if ((isCreatingProductSales || isEditingProductSales) && inventoryProducts.length === 0) {
      fetchInventory();
    }
  }, [newChallengeCondition, isCreateModalOpen, isEditModalOpen, selectedChallenge, inventoryProducts.length, fetchInventory]);

  // Ask the backend which of the given products are already tracked by other active challenges
  const fetchProductConflicts = useCallback(async (productTargets, excludeChallengeId = null) => {
    const productNames = [...new Set(
      (productTargets || [])
        .map((p) => (p.productName || "").toString().trim())
        .filter(Boolean)
    )];

    if (productNames.length === 0) return [];

    try {
      const response = await axios.post(
        `${API_URL}/api/challenges/check-conflicts`,
        { productNames, ...(excludeChallengeId ? { excludeChallengeId } : {}) },
        { headers: getAuthHeaders() }
      );
      return response.data?.conflicts || [];
    } catch (error) {
      console.error("Error checking product conflicts:", error);
      return [];
    }
  }, [getAuthHeaders]);

  // Re-check for conflicts whenever the create-form product selection changes
  const newConflictsRequestRef = useRef(0);
  useEffect(() => {
    if (!(isCreateModalOpen && newChallengeCondition === "product_sales_target") || newChallengeProductTargets.length === 0) {
      setNewChallengeConflicts([]);
      setIsCheckingNewConflicts(false);
      return;
    }

    const requestId = ++newConflictsRequestRef.current;
    setIsCheckingNewConflicts(true);
    fetchProductConflicts(newChallengeProductTargets).then((conflicts) => {
      if (requestId === newConflictsRequestRef.current) {
        setNewChallengeConflicts(conflicts);
        setIsCheckingNewConflicts(false);
      }
    });
  }, [newChallengeProductTargets, newChallengeCondition, isCreateModalOpen, fetchProductConflicts]);

  // Re-check for conflicts whenever the edit-form product selection changes
  const editConflictsRequestRef = useRef(0);
  useEffect(() => {
    const isEditingProductSales = isEditModalOpen && selectedChallenge?.completionCondition === "product_sales_target";
    if (!isEditingProductSales || editChallengeProductTargets.length === 0) {
      setEditChallengeConflicts([]);
      setIsCheckingEditConflicts(false);
      return;
    }

    const requestId = ++editConflictsRequestRef.current;
    setIsCheckingEditConflicts(true);
    fetchProductConflicts(editChallengeProductTargets, selectedChallenge._id).then((conflicts) => {
      if (requestId === editConflictsRequestRef.current) {
        setEditChallengeConflicts(conflicts);
        setIsCheckingEditConflicts(false);
      }
    });
  }, [editChallengeProductTargets, isEditModalOpen, selectedChallenge, fetchProductConflicts]);

  const uniqueLabos = useMemo(() => {
    if (!inventoryProducts) return [];
    const labs = inventoryProducts
      .map((p) => p.labo)
      .filter((labo) => labo && labo.trim() !== "");
    return [...new Set(labs)].sort();
  }, [inventoryProducts]);

  const handleLaboToggle = (laboName, isChecked, isEdit = false) => {
    if (isChecked) {
      setSelectedLabos((prev) => [...prev, laboName]);
      
      const labProducts = inventoryProducts.filter((p) => p.labo === laboName);
      const uniqueProductsMap = new Map();
      
      labProducts.forEach((p) => {
        const name = (p.specialite || p.productName || "");
        if (name && !uniqueProductsMap.has(name)) {
          uniqueProductsMap.set(name, p);
        }
      });

      const setter = isEdit ? setEditChallengeProductTargets : setNewChallengeProductTargets;
      
      setter((prev) => {
        const currentNames = prev.map((target) => (target.productName || "").trim());
        const newTargets = [];

        Array.from(uniqueProductsMap.values()).forEach((p) => {
          const name = (p.specialite || p.productName || "").trim();
          if (!currentNames.includes(name)) {
            newTargets.push({
              art_code: p.art_code || undefined,
              barcode: p.barcode || p.code_barre || undefined,
              productName: name,
              targetSales: "1",
              labo: laboName,
              qte_stock: p.qte_stock,
            });
          }
        });

        return [...prev, ...newTargets];
      });
    } else {
      setSelectedLabos((prev) => prev.filter((l) => l !== laboName));
      const setter = isEdit ? setEditChallengeProductTargets : setNewChallengeProductTargets;
      setter((prev) => prev.filter((p) => p.labo !== laboName));
    }
  };

  const computeSelectedPeriod = () => {
    if (schedulePeriodType === "custom") {
      return {
        value: Number(customPeriodValue),
        unit: customPeriodUnit,
      };
    }

    const presetMap = {
      one_week: { value: 1, unit: "weeks" },
      one_month: { value: 1, unit: "months" },
      three_months: { value: 3, unit: "months" },
      six_months: { value: 6, unit: "months" },
      one_year: { value: 12, unit: "months" },
    };

    return presetMap[schedulePeriodType] || { value: 1, unit: "weeks" };
  };

  const handleSavePeriod = async () => {
    try {
      setErrorMessage("");
      const period = computeSelectedPeriod();

      await axios.put(
        `${API_URL}/api/profilePointsResetSchedule`,
        { period },
        { headers: getAuthHeaders() }
      );

      setCurrentPeriod(period);
      setSuccessMessage("Période de challenge mise à jour avec succès !");
      onPeriodModalClose();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving period:", error);
      setErrorMessage("Erreur lors de la sauvegarde de la période.");
    }
  };

  const submitStartChallenge = async (payload) => {
    try {
      // Activate dynamic waiting overlay
      setIsChallengeStarting(true);

      await axios.post(`${API_URL}/api/challenges/start`, payload, {
        headers: getAuthHeaders(),
      });

      setSuccessMessage("Challenge démarré avec succès !");
      setNewChallengeName("");
      setNewChallengeCondition("end_date");
      setNewChallengeRevenueTarget("");
      setNewChallengeProductTargets([]);
      setSelectedLabos([]);
      setCollapsedGroups({});
      onCreateModalClose();
      fetchChallenges();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error starting challenge:", error);
      setErrorMessage(error.response?.data?.message || "Erreur lors du démarrage du challenge.");
    } finally {
      setIsChallengeStarting(false);
    }
  };

  const handleStartChallenge = async () => {
    setErrorMessage("");

    if (!currentPeriod) {
      setErrorMessage("Veuillez d'abord configurer une période de challenge.");
      return;
    }

    if (newChallengeCondition === "revenue_target") {
      const val = Number(newChallengeRevenueTarget);
      if (!newChallengeRevenueTarget || isNaN(val) || val < 1) {
        setErrorMessage("Veuillez saisir un objectif de chiffre d'affaires valide (> 0).");
        return;
      }
    }
    if (newChallengeCondition === "product_sales_target") {
      if (newChallengeProductTargets.length === 0) {
        setErrorMessage("Veuillez sélectionner au moins un produit.");
        return;
      }
      for (let i = 0; i < newChallengeProductTargets.length; i++) {
        const p = newChallengeProductTargets[i];
        const t = Number(p.targetSales);
        if (!p.productName || !p.productName.toString().trim() || isNaN(t) || t < 1) {
          setErrorMessage(`Produit ${p.productName || i + 1} : le nom et l'objectif de ventes sont requis (objectif ≥ 1).`);
          return;
        }
      }
    }

    const payload = {};
    if (newChallengeName.trim()) payload.name = newChallengeName.trim();

    payload.completionCondition = newChallengeCondition;

    if (newChallengeCondition === "revenue_target") {
      payload.revenueTarget = Number(newChallengeRevenueTarget);
    }
    if (newChallengeCondition === "product_sales_target") {
      payload.productSalesTargets = newChallengeProductTargets.map((p) => ({
        productName: p.productName || undefined,
        barcode: p.barcode,
        art_code: p.art_code,
        targetSales: Number(p.targetSales),
      }));
    }

    if (newChallengeCondition === "product_sales_target" && newChallengeConflicts.length > 0) {
      setConflictConfirmContext({
        conflicts: newChallengeConflicts,
        onConfirm: () => submitStartChallenge(payload),
      });
      onConflictConfirmOpen();
      return;
    }

    await submitStartChallenge(payload);
  };

  const handleEvaluateCondition = async (challenge) => {
    try {
      setEvaluationResult(null);
      setIsEvalLoading(true);
      setSelectedChallenge(challenge);
      const response = await axios.get(
        `${API_URL}/api/challenges/${challenge._id}/evaluate`,
        { headers: getAuthHeaders() }
      );

      setEvaluationsById(prev => ({ ...prev, [challenge._id]: response.data }));

      if (response.data && response.data.completed && challenge.isActive) {
        setConditionMetEvaluation(response.data);
        onConditionMetModalOpen();
      } else {
        setEvaluationResult(response.data);
        onEvalModalOpen();
      }
    } catch (error) {
      console.error("Error evaluating challenge:", error);
      setEvaluationResult({ error: error.response?.data?.message || "Erreur lors de l'évaluation." });
    } finally {
      setIsEvalLoading(false);
    }
  };

  const handleCompleteByCondition = async (challenge) => {
    try {
      setErrorMessage("");

      await axios.post(
        `${API_URL}/api/challenges/${challenge._id}/complete`,
        { completionType: 'condition met' },
        { headers: getAuthHeaders() }
      );

      setSuccessMessage("Challenge terminé (condition atteinte) !");
      onEvalModalClose();
      fetchChallenges();
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error completing challenge by condition:", error);
      setErrorMessage(error.response?.data?.message || "Erreur lors de la complétion du challenge.");
    }
  };

  // MERGED: keeps both labo field (HEAD) and qte_stock field (other branch)
  const addInventoryProduct = (product, isEdit = false) => {
    const defaultProduct = {
      art_code: product.art_code || undefined,
      barcode: product.barcode || product.code_barre || undefined,
      productName: (product.specialite || product.productName || "").trim(),
      targetSales: "",
      labo: product.labo || "Autre",
      qte_stock: product.qte_stock,
    };
    
    if (isEdit) {
      setEditChallengeProductTargets((prev) => [...prev, defaultProduct]);
    } else {
      setNewChallengeProductTargets((prev) => [...prev, defaultProduct]);
    }
  };

  const removeProductTarget = (originalIndex, isEdit = false) => {
    if (isEdit) {
      setEditChallengeProductTargets((prev) => prev.filter((_, i) => i !== originalIndex));
    } else {
      setNewChallengeProductTargets((prev) => prev.filter((_, i) => i !== originalIndex));
    }
  };

  const updateProductTarget = (originalIndex, field, value, isEdit = false) => {
    if (isEdit) {
      setEditChallengeProductTargets((prev) =>
        prev.map((p, i) => (i === originalIndex ? { ...p, [field]: value } : p))
      );
    } else {
      setNewChallengeProductTargets((prev) =>
        prev.map((p, i) => (i === originalIndex ? { ...p, [field]: value } : p))
      );
    }
  };

  // Removes a selected product by name, used from the conflict warning
  const removeProductByName = (productName, isEdit = false) => {
    const normalized = (productName || "").toString().trim().toLowerCase();
    const setter = isEdit ? setEditChallengeProductTargets : setNewChallengeProductTargets;
    setter((prev) => prev.filter((p) => (p.productName || "").toString().trim().toLowerCase() !== normalized));
  };

  const isProductSelected = (product, isEdit = false) => {
    const name = (product.specialite || product.productName || "").toString();
    if (isEdit) {
      return editChallengeProductTargets.some((p) => (p.productName || "").toString() === name);
    }
    return newChallengeProductTargets.some((p) => (p.productName || "").toString() === name);
  };

  const isEditProductTargetsValid = () => {
    if (selectedChallenge?.completionCondition !== "product_sales_target") return true;
    if (editChallengeProductTargets.length === 0) return false;
    return editChallengeProductTargets.every(t => t.targetSales && Number(t.targetSales) > 0);
  };

  const toggleGroupCollapse = (laboName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [laboName]: !prev[laboName]
    }));
  };

  // MERGED: renderGroupedTargets now also shows qte_stock badge on each product card
  // Warns the pharmacist that some selected products are already tracked by other active challenges
  const renderConflictWarning = (conflicts, isEdit) => {
    if (!conflicts || conflicts.length === 0) return null;

    return (
      <Alert status="warning" borderRadius="md" alignItems="flex-start">
        <AlertIcon />
        <Box flex="1">
          <Text fontSize="sm" fontWeight="bold">
            {conflicts.length > 1 ? "Produits déjà suivis ailleurs" : "Produit déjà suivi ailleurs"}
          </Text>
          <VStack align="stretch" spacing={1.5}>
            {conflicts.map((conflict) => (
              <Flex
                key={conflict.productName}
                justify="space-between"
                align="center"
                fontSize="xs"
                bg="orange.50"
                _dark={{ bg: "whiteAlpha.100" }}
                borderRadius="md"
                p={2}
              >
                <Box>
                  <Text fontWeight="semibold">{conflict.productName}</Text>
                  <Text color="gray.600">
                    Déjà dans : {conflict.challenges.map((c) => c.name).join(", ")}
                  </Text>
                </Box>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => removeProductByName(conflict.productName, isEdit)}
                >
                  Retirer
                </Button>
              </Flex>
            ))}
          </VStack>
        </Box>
      </Alert>
    );
  };

  const renderGroupedTargets = (targets, isEdit) => {
    const targetsWithOriginalIndex = targets.map((t, index) => ({ ...t, originalIndex: index }));
    
    const grouped = targetsWithOriginalIndex.reduce((acc, target) => {
      const labo = target.labo || "Autre";
      if (!acc[labo]) acc[labo] = [];
      acc[labo].push(target);
      return acc;
    }, {});

    return Object.entries(grouped).map(([labo, laboTargets]) => {
      const isCollapsed = collapsedGroups[labo];
      
      return (
        <Box key={labo} mb={isCollapsed ? 2 : 4}>
          <Flex 
            align="center" 
            mb={isCollapsed ? 0 : 3} 
            cursor="pointer" 
            onClick={() => toggleGroupCollapse(labo)}
          >
            <Divider />
            <Flex align="center" px={3}>
              <Text fontSize="xs" fontWeight="bold" color="teal.600" textTransform="uppercase" whiteSpace="nowrap" mr={1}>
                {labo} ({laboTargets.length})
              </Text>
              {isCollapsed ? <ChevronDownIcon color="teal.600" /> : <ChevronUpIcon color="teal.600" />}
            </Flex>
            <Divider />
          </Flex>
          
          <Collapse in={!isCollapsed} animateOpacity>
            <VStack spacing={3} align="stretch" mt={isCollapsed ? 0 : 2}>
              {laboTargets.map((product) => (
                <Box key={product.originalIndex} border="1px solid" borderColor={borderColor} borderRadius="md" p={3}>
                  <Flex justify="space-between" align="center" mb={2}>
                    <HStack spacing={2}>
                      <Text fontSize="sm" fontWeight="bold">
                        {product.productName || "Produit sélectionné"}
                      </Text>
                      {product.qte_stock !== undefined && (
                        <Box
                          bg="gray.300"
                          color="gray.600"
                          px={1.5}
                          py={0.5}
                          borderRadius="md"
                          fontSize="xs"
                          fontWeight="medium"
                          whiteSpace="nowrap"
                        >
                          Stock : {product.qte_stock}
                        </Box>
                      )}
                    </HStack>
                    <Tag
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      cursor="pointer"
                      onClick={() => removeProductTarget(product.originalIndex, isEdit)}
                    >
                      <TagLabel>Retirer</TagLabel>
                      <TagCloseButton />
                    </Tag>
                  </Flex>
                  <HStack spacing={2}>
                    <FormControl isRequired w="150px">
                      <FormLabel fontSize="xs">Objectif ventes</FormLabel>
                      <NumberInput
                        size="sm"
                        min={1}
                        value={product.targetSales}
                        onChange={(val) => updateProductTarget(product.originalIndex, "targetSales", val, isEdit)}
                      >
                        <NumberInputField placeholder="1" />
                      </NumberInput>
                    </FormControl>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Collapse>
        </Box>
      );
    });
  };

  const submitEditChallenge = async (updates) => {
    try {
      await axios.put(
        `${API_URL}/api/challenges/${selectedChallenge._id}`,
        updates,
        { headers: getAuthHeaders() }
      );

      setSuccessMessage("Challenge modified with success !");
      onEditModalClose();
      fetchChallenges();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error updating challenge:", error);
      setErrorMessage(error.response?.data?.message || "Erreur lors de la modification du challenge.");
    }
  };

  const handleEditChallenge = async () => {
    setErrorMessage("");

    const updates = {};
    if (editChallengeName.trim()) {
      updates.name = editChallengeName.trim();
    }
    if (editChallengeStartDate) {
      updates.startDate = new Date(editChallengeStartDate).toISOString();
    }
    if (editChallengeEndDate) {
      updates.endDate = new Date(editChallengeEndDate).toISOString();
    }

    if (selectedChallenge?.completionCondition === "product_sales_target") {
      if (!isEditProductTargetsValid()) {
        setErrorMessage("Veuillez sélectionner au moins un produit et définir un objectif de ventes valide pour chaque produit.");
        return;
      }
      let targetsChanged = false;
      const origTargets = originalEditValues.productSalesTargets || [];
      if (editChallengeProductTargets.length !== origTargets.length) {
        targetsChanged = true;
      } else {
        for (let i = 0; i < editChallengeProductTargets.length; i++) {
          if (
            editChallengeProductTargets[i].productName !== origTargets[i].productName ||
            Number(editChallengeProductTargets[i].targetSales) !== Number(origTargets[i].targetSales)
          ) {
            targetsChanged = true;
            break;
          }
        }
      }
      if (targetsChanged) {
        updates.productSalesTargets = editChallengeProductTargets.map(t => ({
           productName: t.productName,
           barcode: t.barcode,
           art_code: t.art_code,
           targetSales: Number(t.targetSales)
        }));
      }
    }

    if (Object.keys(updates).length === 0) {
      setErrorMessage("Veuillez modifier au moins un champ.");
      return;
    }

    if (selectedChallenge?.completionCondition === "product_sales_target" && editChallengeConflicts.length > 0) {
      setConflictConfirmContext({
        conflicts: editChallengeConflicts,
        onConfirm: () => submitEditChallenge(updates),
      });
      onConflictConfirmOpen();
      return;
    }

    await submitEditChallenge(updates);
  };

  const handleConflictConfirmContinue = async () => {
    const action = conflictConfirmContext?.onConfirm;
    onConflictConfirmClose();
    setConflictConfirmContext(null);
    if (action) {
      await action();
    }
  };

  const handleConflictConfirmCancel = () => {
    onConflictConfirmClose();
    setConflictConfirmContext(null);
  };

  const handleCompleteChallenge = async () => {
    try {
      setErrorMessage("");

      await axios.post(
        `${API_URL}/api/challenges/${selectedChallenge._id}/complete`,
        {},
        { headers: getAuthHeaders() }
      );

      setSuccessMessage("Challenge terminé avec succès !");
      onCompleteModalClose();
      fetchChallenges();
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error completing challenge:", error);
      setErrorMessage(error.response?.data?.message || "Erreur lors de la complétion du challenge.");
    }
  };

  const handleDeleteChallenge = async () => {
    try {
      setErrorMessage("");

      await axios.delete(`${API_URL}/api/challenges/${selectedChallenge._id}`, {
        headers: getAuthHeaders(),
      });

      setSuccessMessage("Challenge supprimé avec succès !");
      onDeleteModalClose();
      fetchChallenges();
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting challenge:", error);
      setErrorMessage(error.response?.data?.message || "Erreur lors de la suppression du challenge.");
    }
  };

  const fetchChallengeStats = async (challengeId) => {
    try {
      setIsStatsLoading(true);
      const response = await axios.get(
        `${API_URL}/api/challenges/${challengeId}/stats`,
        { headers: getAuthHeaders() }
      );
      setChallengeStats(response.data);
    } catch (error) {
      console.error("Error fetching challenge stats:", error);
      setChallengeStats(null);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const openEditModal = (challenge) => {
    setSelectedChallenge(challenge);
    const formattedStartDate = formatDateForInput(challenge.startDate);
    const formattedEndDate = formatDateForInput(challenge.endDate);
    setEditChallengeName(challenge.name);
    setEditChallengeStartDate(formattedStartDate);
    setEditChallengeEndDate(formattedEndDate);
    
    const initialTargets = challenge.productSalesTargets ? challenge.productSalesTargets.map(t => {
      const invMatch = inventoryProducts.find(p => (p.specialite || p.productName || "").trim() === (t.productName || "").trim());
      return {
        ...t,
        labo: invMatch?.labo || "Autre",
        qte_stock: invMatch?.qte_stock,
      };
    }) : [];

    setEditChallengeProductTargets(initialTargets);
    setOriginalEditValues({
      name: challenge.name,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      productSalesTargets: initialTargets,
    });
    setErrorMessage("");
    setSelectedLabos([]);
    setCollapsedGroups({});
    onEditModalOpen();
  };

  const openCompleteModal = (challenge) => {
    setSelectedChallenge(challenge);
    setErrorMessage("");
    onCompleteModalOpen();
  };

  const openDeleteModal = (challenge) => {
    setSelectedChallenge(challenge);
    setErrorMessage("");
    onDeleteModalOpen();
  };

  const openStatsModal = (challenge) => {
    setSelectedChallenge(challenge);
    fetchChallengeStats(challenge._id);
    onStatsModalOpen();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const getChallengeProgress = (challenge) => {
    if (!challenge) return 0;

    const condition = challenge.completionCondition || 'end_date';

    const evalForThis = evaluationsById && evaluationsById[String(challenge._id)];

    if (condition === 'revenue_target') {
      if (evalForThis && typeof evalForThis.progressPct === 'number') {
        return Math.round(evalForThis.progressPct);
      }
    }

    if (condition === 'product_sales_target') {
      if (evalForThis && Array.isArray(evalForThis.productResults) && evalForThis.productResults.length > 0) {
        const prs = evalForThis.productResults;
        const sum = prs.reduce((s, p) => s + (p.progressPct || 0), 0);
        return Math.round(sum / prs.length);
      }
    }

    const now = new Date();
    const start = new Date(challenge.startDate);
    const end = new Date(challenge.endDate);

    if (now < start) return 0;
    if (now > end) return 100;

    const total = end - start;
    const elapsed = now - start;
    return Math.round((elapsed / total) * 100);
  };

  const getConditionLabel = (condition) => {
    const labels = {
      end_date:             "Date de fin",
      revenue_target:       "Objectif CA",
      product_sales_target: "Objectif ventes produit",
    };
    return labels[condition] || condition || "Date de fin";
  };

  const getConditionScheme = (condition) => {
    const schemes = {
      end_date:             "blue",
      revenue_target:       "teal",
      product_sales_target: "orange",
    };
    return schemes[condition] || "gray";
  };

  const getRemainingTime = (challenge) => {
    const now = new Date();
    const end = new Date(challenge.endDate);
    const diff = end - now;

    if (diff <= 0) return "Terminé";

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / (60 * 60 * 24));
    const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);

    if (days > 0) {
      return `${days} jour${days > 1 ? "s" : ""} restant${days > 1 ? "s" : ""}`;
    }
    if (days === 0 && hours > 0) {
      return `${hours} heure${hours > 1 ? "s" : ""} restante${hours > 1 ? "s" : ""}`;
    }
    if (hours === 0 && minutes > 0) {
      return `${minutes} minute${minutes > 1 ? "s" : ""} restante${minutes > 1 ? "s" : ""}`;
    }
  };

  const formatPeriod = (period) => {
    if (!period) return "Non configurée";
    const { value, unit } = period;
    if (unit === "weeks") {
      return value === 1 ? "1 semaine" : `${value} semaines`;
    }
    if (unit === "months") {
      return value === 1 ? "1 mois" : `${value} mois`;
    }
    return `${value} ${unit}`;
  };

  const isCustomPeriodValid = () => {
    if (schedulePeriodType !== "custom") return true;
    if (customPeriodValue === "") return false;
    const n = Number(customPeriodValue);
    return Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 36;
  };

  const isEditDatesValid = () => {
    if (!editChallengeStartDate || !editChallengeEndDate) return true;
    const start = new Date(editChallengeStartDate);
    const end = new Date(editChallengeEndDate);
    return end > start;
  };

  const isMinimumDurationValid = () => {
    if (!editChallengeStartDate || !editChallengeEndDate) return true;
    const start = new Date(editChallengeStartDate);
    const end = new Date(editChallengeEndDate);
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
    return (end.getTime() - start.getTime()) >= oneWeekInMs;
  };

  const hasEditChanges = () => {
    let targetsChanged = false;
    if (selectedChallenge?.completionCondition === "product_sales_target") {
      const origTargets = originalEditValues.productSalesTargets || [];
      if (editChallengeProductTargets.length !== origTargets.length) {
        targetsChanged = true;
      } else {
        for (let i = 0; i < editChallengeProductTargets.length; i++) {
          if (
            editChallengeProductTargets[i].productName !== origTargets[i].productName ||
            Number(editChallengeProductTargets[i].targetSales) !== Number(origTargets[i].targetSales)
          ) {
            targetsChanged = true;
            break;
          }
        }
      }
    }

    return (
      editChallengeName !== originalEditValues.name ||
      editChallengeStartDate !== originalEditValues.startDate ||
      editChallengeEndDate !== originalEditValues.endDate ||
      targetsChanged
    );
  };

  const getMinEndDate = () => {
    if (!editChallengeStartDate) return "";
    const start = new Date(editChallengeStartDate);
    start.setMinutes(start.getMinutes() + 1);
    return start.toISOString().slice(0, 16);
  };

  const getMaxStartDate = () => {
    if (!editChallengeEndDate) return "";
    const end = new Date(editChallengeEndDate);
    end.setMinutes(end.getMinutes() - 1);
    return end.toISOString().slice(0, 16);
  };

  return (
    <Box pt={{ base: "140px", md: "80px", xl: "80px" }}>
      {successMessage && (
        <Alert status="success" mb={4} borderRadius="md">
          <AlertIcon />
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {errorMessage}
        </Alert>
      )}

      {/* Active Challenge Card */}
      <Box
        bg={cardBg}
        p={6}
        borderRadius="xl"
        boxShadow="md"
        mb={6}
        border="1px solid"
        borderColor={borderColor}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Challenges Actifs
          </Text>
          <HStack spacing={3}>
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={onPeriodModalOpen}
            >
              Configurer la période
            </Button>
            {true && (
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<AddIcon />}
                onClick={() => {
                  setNewChallengeName("");
                  setErrorMessage("");
                  setNewChallengeProductTargets([]);
                  setSelectedLabos([]);
                  setIsLaboSelectorOpen(false);
                  setCollapsedGroups({});
                  onCreateModalOpen();
                }}
                isDisabled={!currentPeriod}
              >
                Démarrer un challenge
              </Button>
            )}
          </HStack>
        </Flex>

        {(challenges.filter(c => c.isActive).length > 0) ? (
          <VStack spacing={4} align="stretch">
            {challenges.filter(c => c.isActive).map(activeChallenge => (
            <Box key={activeChallenge._id} p={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
              <Flex justify="space-between" align="flex-start" mb={4}>
              <Box>
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  {activeChallenge.name}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {formatDate(activeChallenge.startDate)} → {formatDate(activeChallenge.endDate)}
                </Text>
                <Badge
                  mt={1}
                  colorScheme={getConditionScheme(activeChallenge.completionCondition)}
                  variant="subtle"
                  fontSize="xs"
                >
                  Clôture : {getConditionLabel(activeChallenge.completionCondition)}
                </Badge>
              </Box>
              <HStack spacing={2}>
                {["revenue_target", "product_sales_target"].includes(
                  activeChallenge.completionCondition
                ) && (
                  <Tooltip label="Évaluer la condition de clôture">
                    <IconButton
                      size="sm"
                      colorScheme={getConditionScheme(activeChallenge.completionCondition)}
                      icon={<InfoOutlineIcon />}
                      onClick={() => handleEvaluateCondition(activeChallenge)}
                    />
                  </Tooltip>
                )}
                <Tooltip label="Voir les statistiques">
                  <IconButton
                    size="sm"
                    colorScheme="blue"
                    icon={<ViewIcon />}
                    onClick={() => openStatsModal(activeChallenge)}
                  />
                </Tooltip>
                <Tooltip label="Modifier">
                  <IconButton
                    size="sm"
                    colorScheme="orange"
                    icon={<EditIcon />}
                    onClick={() => openEditModal(activeChallenge)}
                  />
                </Tooltip>
                <Tooltip label="Terminer le challenge">
                  <IconButton
                    size="sm"
                    colorScheme="green"
                    icon={<CheckIcon />}
                    onClick={() => openCompleteModal(activeChallenge)}
                  />
                </Tooltip>
              </HStack>
            </Flex>

            <Box mb={4}>
              <Flex justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.500">
                  Progression
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {getChallengeProgress(activeChallenge)}%
                </Text>
              </Flex>
              <Progress
                w="100%"
                bg={trackBg}
                value={getChallengeProgress(activeChallenge)}
                size="sm"
                colorScheme="teal"
                borderRadius="full"
              />
            </Box>

            <Badge colorScheme="green" fontSize="sm">
              {getRemainingTime(activeChallenge)}
            </Badge>
          </Box>
          ))}
          </VStack>
        ) : (
          <Box textAlign="center" py={6}>
            <Text color="gray.500" mb={2}>
              Aucun challenge actif
            </Text>
            <Text fontSize="sm" color="gray.400">
              Période configurée : {formatPeriod(currentPeriod)}
            </Text>
            {!currentPeriod && (
              <Text fontSize="sm" color="orange.400" mt={2}>
                Configurez d'abord une période pour pouvoir démarrer un challenge.
              </Text>
            )}
          </Box>
        )}
      </Box>

      {/* Challenges History */}
      <Box
        bg={cardBg}
        p={6}
        borderRadius="xl"
        boxShadow="md"
        border="1px solid"
        borderColor={borderColor}
      >
        <Text fontSize="xl" fontWeight="bold" mb={4} color={textColor}>
          Historique des Challenges
        </Text>

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="teal.500" />
          </Flex>
        ) : challenges.length === 0 ? (
          <Text color="gray.500" textAlign="center" py={8}>
            Aucun challenge créé pour le moment.
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Nom</Th>
                  <Th>Date de début</Th>
                  <Th>Date de fin</Th>
                  <Th>Statut</Th>
                  <Th>Condition</Th>
                  <Th>Complétion</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {challenges.map((challenge) => (
                  <Tr key={challenge._id}>
                    <Td fontWeight="medium">{challenge.name}</Td>
                    <Td>{formatDate(challenge.startDate)}</Td>
                    <Td>{formatDate(challenge.endDate)}</Td>
                    <Td>
                      <Badge colorScheme={challenge.isActive ? "green" : "gray"}>
                        {challenge.isActive ? "Actif" : "Terminé"}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={getConditionScheme(challenge.completionCondition)}
                        variant="subtle"
                      >
                        {getConditionLabel(challenge.completionCondition)}
                      </Badge>
                    </Td>
                    <Td>
                      {challenge.completionType === "auto" && (
                        <Badge colorScheme="blue">Auto</Badge>
                      )}
                      {challenge.completionType === "manual" && (
                        <Badge colorScheme="purple">Manuel</Badge>
                      )}
                      {challenge.completionType === "condition met" && (
                        <Badge colorScheme="green">Condition atteinte</Badge>
                      )}
                      {!challenge.completionType && challenge.isActive && (
                        <Badge colorScheme="yellow">En cours</Badge>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        {challenge.isActive &&
                          ["revenue_target", "product_sales_target"].includes(
                            challenge.completionCondition
                          ) && (
                            <Tooltip label="Évaluer la condition">
                              <IconButton
                                size="xs"
                                colorScheme={getConditionScheme(challenge.completionCondition)}
                                variant="ghost"
                                icon={<InfoOutlineIcon />}
                                onClick={() => handleEvaluateCondition(challenge)}
                              />
                            </Tooltip>
                          )}
                        <Tooltip label="Voir les statistiques">
                          <IconButton
                            size="xs"
                            colorScheme="blue"
                            variant="ghost"
                            icon={<ViewIcon />}
                            onClick={() => openStatsModal(challenge)}
                          />
                        </Tooltip>
                        {challenge.isActive && (
                          <Tooltip label="Modifier">
                            <IconButton
                              size="xs"
                              colorScheme="orange"
                              variant="ghost"
                              icon={<EditIcon />}
                              onClick={() => openEditModal(challenge)}
                            />
                          </Tooltip>
                        )}
                        {challenge.isActive && (
                          <Tooltip label="Terminer">
                            <IconButton
                              size="xs"
                              colorScheme="green"
                              variant="ghost"
                              icon={<CheckIcon />}
                              onClick={() => openCompleteModal(challenge)}
                            />
                          </Tooltip>
                        )}
                        {!challenge.isActive && (
                          <Tooltip label="Supprimer">
                            <IconButton
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              icon={<DeleteIcon />}
                              onClick={() => openDeleteModal(challenge)}
                            />
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Period Configuration Modal */}
      <Modal isOpen={isPeriodModalOpen} onClose={onPeriodModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Configurer la période de challenge</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.500">
                Cette période détermine la durée de chaque nouveau challenge.
              </Text>

              <FormControl>
                <FormLabel>Période</FormLabel>
                <Select
                  value={schedulePeriodType}
                  onChange={(e) => setSchedulePeriodType(e.target.value)}
                >
                  <option value="one_week">Une semaine</option>
                  <option value="one_month">Un mois</option>
                  <option value="three_months">3 mois</option>
                  <option value="six_months">6 mois</option>
                  <option value="one_year">Un an</option>
                  <option value="custom">Personnalisée</option>
                </Select>
              </FormControl>

              {schedulePeriodType === "custom" && (
                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel>Valeur</FormLabel>
                    <NumberInput
                      value={customPeriodValue}
                      onChange={(value) => setCustomPeriodValue(value)}
                      min={1}
                      max={36}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Unité</FormLabel>
                    <Select
                      value={customPeriodUnit}
                      onChange={(e) => setCustomPeriodUnit(e.target.value)}
                    >
                      <option value="weeks">Semaines</option>
                      <option value="months">Mois</option>
                    </Select>
                  </FormControl>
                </HStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleSavePeriod}
              mr={3}
              isDisabled={!isCustomPeriodValid()}
            >
              Sauvegarder
            </Button>
            <Button variant="ghost" onClick={onPeriodModalClose}>
              Annuler
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Challenge Modal with Embedded Dynamic Waiting Screen */}
      <Modal 
        isOpen={isCreateModalOpen} 
        onClose={onCreateModalClose} 
        size="lg"
        closeOnOverlayClick={!isChallengeStarting}
        closeOnEsc={!isChallengeStarting}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Démarrer un nouveau challenge</ModalHeader>
          {!isChallengeStarting && <ModalCloseButton />}
          <ModalBody>
            {isChallengeStarting ? (
              <VStack spacing={6} py={10} align="center">
                <Spinner size="xl" color="teal.500" thickness="4px" speed="0.65s" />
                <Text fontSize="lg" fontWeight="semibold" textAlign="center" color={textColor}>
                  Création du challenge en cours...
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center" px={4}>
                  Nous configurons vos objectifs et mettons à jour vos produits favoris en stock. Veuillez patienter et ne pas fermer cette page.
                </Text>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                {errorMessage && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {errorMessage}
                  </Alert>
                )}

                <Text fontSize="sm" color="gray.500">
                  Le challenge commencera immédiatement et durera {formatPeriod(currentPeriod)}.
                </Text>

                <FormControl>
                  <FormLabel>Nom du challenge (optionnel)</FormLabel>
                  <Input
                    placeholder="Ex: Challenge Janvier 2026"
                    value={newChallengeName}
                    onChange={(e) => setNewChallengeName(e.target.value)}
                  />
                  <FormHelperText>Si vide, un nom sera généré automatiquement.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Condition de clôture</FormLabel>
                  <Select
                    value={newChallengeCondition}
                    onChange={(e) => {
                      setNewChallengeCondition(e.target.value);
                      setNewChallengeRevenueTarget("");
                      setNewChallengeProductTargets([]);
                      setSelectedLabos([]);
                      setIsLaboSelectorOpen(false);
                      setCollapsedGroups({});
                    }}
                  >
                    <option value="end_date">1. Date de fin</option>
                    <option value="revenue_target">2. Objectif chiffre d'affaires</option>
                    <option value="product_sales_target">3. Objectif ventes par produit / laboratoire</option>
                  </Select>
                  <FormHelperText>
                    {newChallengeCondition === "end_date" &&
                      "Le challenge se ferme automatiquement à la date de fin."}
                    {newChallengeCondition === "revenue_target" &&
                      "Le challenge se ferme dès que le CA total atteint l'objectif."}
                    {newChallengeCondition === "product_sales_target" &&
                      "Sélectionnez un ou plusieurs laboratoires pour ajouter en masse leurs produits, ou cherchez-les individuellement."}
                  </FormHelperText>
                </FormControl>

                {newChallengeCondition === "revenue_target" && (
                  <FormControl isRequired>
                    <FormLabel>Objectif de chiffre d'affaires ({CURRENCY.symbol})</FormLabel>
                    <NumberInput
                      min={1}
                      value={newChallengeRevenueTarget}
                      onChange={(val) => setNewChallengeRevenueTarget(val)}
                    >
                      <NumberInputField />
                    </NumberInput>
                    <FormHelperText>
                      {`Montant total en ${CURRENCY.symbol} à atteindre pour clôturer le challenge.`}
                    </FormHelperText>
                  </FormControl>
                )}

                {newChallengeCondition === "product_sales_target" && (
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm" mb={2}>
                      Sélection des produits
                    </Text>

                    {isInventoryLoading ? (
                      <Flex justify="center" py={4}>
                        <Spinner size="sm" color="teal.500" />
                      </Flex>
                    ) : (
                      <VStack spacing={4} align="stretch">

                        {/* Labo bulk-selector */}
                        <Box border="1px solid" borderColor={borderColor} borderRadius="md" p={3} bg={laboBg}>
                          <FormControl>
                            <Flex
                              justify="space-between"
                              align="center"
                              cursor="pointer"
                              onClick={() => setIsLaboSelectorOpen(!isLaboSelectorOpen)}
                            >
                              <Box>
                                <FormLabel fontSize="xs" fontWeight="bold" mb={0} cursor="pointer">
                                  Ajout rapide par Laboratoires
                                </FormLabel>
                                <FormHelperText fontSize="xs" mt={0}>
                                  Cochez pour inclure tous les produits en stock.
                                </FormHelperText>
                              </Box>
                              {isLaboSelectorOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </Flex>

                            <Collapse in={isLaboSelectorOpen} animateOpacity>
                              <Box maxH="150px" overflowY="auto" mt={3} p={1}>
                                <VStack align="start" spacing={2}>
                                  {uniqueLabos.map((lab) => (
                                    <Checkbox
                                      key={lab}
                                      size="sm"
                                      colorScheme="teal"
                                      isChecked={selectedLabos.includes(lab)}
                                      onChange={(e) => handleLaboToggle(lab, e.target.checked, false)}
                                    >
                                      {lab}
                                    </Checkbox>
                                  ))}
                                </VStack>
                              </Box>
                            </Collapse>
                          </FormControl>
                        </Box>

                        {/* Individual product search */}
                        <Box border="1px solid" borderColor={borderColor} borderRadius="md" p={3}>
                          <Input
                            placeholder="Rechercher un produit individuellement..."
                            size="sm"
                            mb={3}
                            value={productSearchTerm}
                            onChange={(e) => setProductSearchTerm(e.target.value)}
                          />
                          <Box maxH="120px" overflowY="auto">
                            {inventoryProducts
                              .filter((p) => !isProductSelected(p, false) && (p.specialite || p.productName || "").toLowerCase().includes(productSearchTerm.toLowerCase()))
                              .sort((a, b) => {
                                const an = (a.specialite || a.productName || "").toLowerCase();
                                const bn = (b.specialite || b.productName || "").toLowerCase();
                                if (an < bn) return -1;
                                if (an > bn) return 1;
                                return 0;
                              })
                              .map((p, idx) => (
                                <Flex
                                  key={p._id || p.med_id || idx}
                                  justify="space-between"
                                  align="center"
                                  p={2}
                                  _hover={{ bg: trackBg, cursor: "pointer" }}
                                  borderRadius="md"
                                  onClick={() => addInventoryProduct(p, false)}
                                >
                                  <Text fontSize="xs" flex={1} minW={0} noOfLines={1} mr={2}>
                                    {p.specialite || p.productName || "Produit sans nom"}
                                  </Text>
                                  <HStack spacing={2} flexShrink={0}>
                                    {p.qte_stock !== undefined && (
                                      <Box
                                        bg="gray.300"
                                        color="gray.600"
                                        px={1.5}
                                        py={0.5}
                                        borderRadius="md"
                                        fontSize="xs"
                                        fontWeight="medium"
                                        whiteSpace="nowrap"
                                      >
                                        Stock : {p.qte_stock}
                                      </Box>
                                    )}
                                    <AddIcon fontSize="xs" color="teal.500" />
                                  </HStack>
                                </Flex>
                              ))}
                            {inventoryProducts.length > 0 && inventoryProducts.filter((p) => !isProductSelected(p, false) && (p.specialite || p.productName || "").toLowerCase().includes(productSearchTerm.toLowerCase())).length === 0 && (
                              <Text fontSize="xs" color="gray.500" textAlign="center" py={2}>
                                Aucun produit disponible.
                              </Text>
                            )}
                          </Box>
                        </Box>

                        {renderConflictWarning(newChallengeConflicts, false)}

                        {/* Selected products grouped by labo */}
                        {newChallengeProductTargets.length > 0 && (
                          <Box>
                            <Flex justify="space-between" align="center" mb={2}>
                              <Text fontWeight="semibold" fontSize="sm">
                                Produits sélectionnés ({newChallengeProductTargets.length})
                              </Text>
                              <Button size="xs" colorScheme="red" variant="ghost" onClick={() => {
                                setNewChallengeProductTargets([]);
                                setSelectedLabos([]);
                              }}>
                                Tout vider
                              </Button>
                            </Flex>
                            <Box maxH="350px" overflowY="auto" pr={1}>
                              {renderGroupedTargets(newChallengeProductTargets, false)}
                            </Box>
                          </Box>
                        )}
                      </VStack>
                    )}
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          {!isChallengeStarting && (
            <ModalFooter>
              <Button
                colorScheme="teal"
                onClick={handleStartChallenge}
                mr={3}
                isDisabled={newChallengeCondition === "product_sales_target" && isCheckingNewConflicts}
              >
                Démarrer
              </Button>
              <Button variant="ghost" onClick={onCreateModalClose}>
                Annuler
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>

      {/* Edit Challenge Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Modifier le challenge</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {errorMessage && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {errorMessage}
                </Alert>
              )}

              <FormControl>
                <FormLabel>Nom du challenge</FormLabel>
                <Input
                  value={editChallengeName}
                  onChange={(e) => setEditChallengeName(e.target.value)}
                />
              </FormControl>

              <FormControl isInvalid={!isEditDatesValid()}>
                <FormLabel>Date de début</FormLabel>
                <Input
                  type="datetime-local"
                  value={editChallengeStartDate}
                  onChange={(e) => setEditChallengeStartDate(e.target.value)}
                  max={getMaxStartDate()}
                />
              </FormControl>

              <FormControl isInvalid={!isEditDatesValid() || !isMinimumDurationValid()}>
                <FormLabel>Date de fin</FormLabel>
                <Input
                  type="datetime-local"
                  value={editChallengeEndDate}
                  onChange={(e) => setEditChallengeEndDate(e.target.value)}
                  min={getMinEndDate()}
                />
                {!isEditDatesValid() && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    La date de fin doit être ultérieure la date de début.
                  </Text>
                )}
                {isEditDatesValid() && !isMinimumDurationValid() && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    La durée minimale d'un challenge est d'une semaine.
                  </Text>
                )}
              </FormControl>

              {selectedChallenge?.completionCondition === "product_sales_target" && (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={2}>
                    Modification des produits
                  </Text>
                  {isInventoryLoading ? (
                    <Flex justify="center" py={4}>
                      <Spinner size="sm" color="teal.500" />
                    </Flex>
                  ) : (
                    <VStack spacing={4} align="stretch">

                      {/* Labo bulk-selector */}
                      <Box border="1px solid" borderColor={borderColor} borderRadius="md" p={3} bg={laboBg}>
                        <FormControl>
                          <Flex
                            justify="space-between"
                            align="center"
                            cursor="pointer"
                            onClick={() => setIsLaboSelectorOpen(!isLaboSelectorOpen)}
                          >
                            <Box>
                              <FormLabel fontSize="xs" fontWeight="bold" mb={0} cursor="pointer">
                                Ajout rapide par Laboratoires
                              </FormLabel>
                              <FormHelperText fontSize="xs" mt={0}>
                                Cochez pour inclure tous les produits en stock.
                              </FormHelperText>
                            </Box>
                            {isLaboSelectorOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          </Flex>

                          <Collapse in={isLaboSelectorOpen} animateOpacity>
                            <Box maxH="150px" overflowY="auto" mt={3} p={1}>
                              <VStack align="start" spacing={2}>
                                {uniqueLabos.map((lab) => (
                                  <Checkbox
                                    key={lab}
                                    size="sm"
                                    colorScheme="teal"
                                    isChecked={selectedLabos.includes(lab)}
                                    onChange={(e) => handleLaboToggle(lab, e.target.checked, true)}
                                  >
                                    {lab}
                                  </Checkbox>
                                ))}
                              </VStack>
                            </Box>
                          </Collapse>
                        </FormControl>
                      </Box>

                      {/* Individual product search */}
                      <Box border="1px solid" borderColor={borderColor} borderRadius="md" p={3}>
                        <Input
                          placeholder="Rechercher un produit à ajouter..."
                          size="sm"
                          mb={3}
                          value={productSearchTerm}
                          onChange={(e) => setProductSearchTerm(e.target.value)}
                        />
                        <Box maxH="150px" overflowY="auto">
                          {inventoryProducts
                            .filter((p) => !isProductSelected(p, true) && (p.specialite || p.productName || "").toLowerCase().includes(productSearchTerm.toLowerCase()))
                            .sort((a, b) => {
                              const an = (a.specialite || a.productName || "").toLowerCase();
                              const bn = (b.specialite || b.productName || "").toLowerCase();
                              if (an < bn) return -1;
                              if (an > bn) return 1;
                              return 0;
                            })
                            .map((p, idx) => (
                              <Flex
                                key={p._id || p.med_id || idx}
                                justify="space-between"
                                align="center"
                                p={2}
                                _hover={{ bg: trackBg, cursor: "pointer" }}
                                borderRadius="md"
                                onClick={() => addInventoryProduct(p, true)}
                              >
                                <Text fontSize="xs" flex={1} minW={0} noOfLines={1} mr={2}>
                                  {p.specialite || p.productName || "Produit sans nom"}
                                </Text>
                                <HStack spacing={2} flexShrink={0}>
                                  {p.qte_stock !== undefined && (
                                    <Box
                                      bg="gray.300"
                                      color="gray.600"
                                      px={1.5}
                                      py={0.5}
                                      borderRadius="md"
                                      fontSize="xs"
                                      fontWeight="medium"
                                      whiteSpace="nowrap"
                                    >
                                      Stock : {p.qte_stock}
                                    </Box>
                                  )}
                                  <AddIcon fontSize="xs" color="teal.500" />
                                </HStack>
                              </Flex>
                            ))}
                          {inventoryProducts.length > 0 && inventoryProducts.filter((p) => !isProductSelected(p, true) && (p.specialite || p.productName || "").toLowerCase().includes(productSearchTerm.toLowerCase())).length === 0 && (
                            <Text fontSize="xs" color="gray.500" textAlign="center" py={2}>
                              Aucun produit disponible.
                            </Text>
                          )}
                        </Box>
                      </Box>

                      {renderConflictWarning(editChallengeConflicts, true)}

                      {/* Selected products grouped by labo */}
                      {editChallengeProductTargets.length > 0 && (
                        <Box>
                          <Flex justify="space-between" align="center" mb={2}>
                            <Text fontWeight="semibold" fontSize="sm">
                              Produits sélectionnés ({editChallengeProductTargets.length})
                            </Text>
                            <Button size="xs" colorScheme="red" variant="ghost" onClick={() => {
                              setEditChallengeProductTargets([]);
                              setSelectedLabos([]);
                            }}>
                              Tout vider
                            </Button>
                          </Flex>
                          <Box maxH="350px" overflowY="auto" pr={1}>
                            {renderGroupedTargets(editChallengeProductTargets, true)}
                          </Box>
                        </Box>
                      )}
                    </VStack>
                  )}
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleEditChallenge}
              mr={3}
              isDisabled={!isEditDatesValid() || !isMinimumDurationValid() || !hasEditChanges() || (selectedChallenge?.completionCondition === "product_sales_target" && (!isEditProductTargetsValid() || isCheckingEditConflicts))}
            >
              Sauvegarder
            </Button>
            <Button variant="ghost" onClick={onEditModalClose}>
              Annuler
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Product Conflict Confirmation Modal */}
      <Modal isOpen={isConflictConfirmOpen} onClose={handleConflictConfirmCancel} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Produit déjà suivi ailleurs</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
              </Alert>

              <VStack align="stretch" spacing={1.5}>
                {(conflictConfirmContext?.conflicts || []).map((conflict) => (
                  <Box
                    key={conflict.productName}
                    fontSize="xs"
                    bg="orange.50"
                    _dark={{ bg: "whiteAlpha.100" }}
                    borderRadius="md"
                    p={2}
                  >
                    <Text fontWeight="semibold">{conflict.productName}</Text>
                    <Text color="gray.600">
                      Déjà dans : {conflict.challenges.map((c) => c.name).join(", ")}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="orange" onClick={handleConflictConfirmContinue} mr={3}>
              Continuer quand même
            </Button>
            <Button variant="ghost" onClick={handleConflictConfirmCancel}>
              Modifier la sélection
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Complete Challenge Modal */}
      <Modal isOpen={isCompleteModalOpen} onClose={onCompleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Terminer le challenge</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Êtes-vous sûr de vouloir terminer le challenge "{selectedChallenge?.name}" ?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" onClick={handleCompleteChallenge} mr={3}>
              Oui, terminer
            </Button>
            <Button variant="ghost" onClick={onCompleteModalClose}>
              Annuler
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Challenge Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Supprimer le challenge</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Êtes-vous sûr de vouloir supprimer le challenge "{selectedChallenge?.name}" ?
            </Text>
            <Alert status="error" mt={4} borderRadius="md">
              <AlertIcon />
              Cette action est irréversible.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={handleDeleteChallenge} mr={3}>
              Oui, supprimer
            </Button>
            <Button variant="ghost" onClick={onDeleteModalClose}>
              Annuler
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Challenge Stats Modal */}
      <Modal isOpen={isStatsModalOpen} onClose={onStatsModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Statistiques : {selectedChallenge?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isStatsLoading ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" color="teal.500" />
              </Flex>
            ) : challengeStats ? (
              <VStack spacing={6} align="stretch">
                <Box>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Stat bg="teal.50" p={3} borderRadius="md">
                      <StatLabel color="teal.700">Total Ventes</StatLabel>
                      <StatNumber color="teal.800" fontSize="lg">
                        {challengeStats.summary.totalSalesDinars?.toLocaleString("fr-FR") || 0} {CURRENCY.symbol}
                      </StatNumber>
                    </Stat>
                    <Stat bg="orange.50" p={3} borderRadius="md">
                      <StatLabel color="orange.700">Ventes Propérimés</StatLabel>
                      <StatNumber color="orange.800" fontSize="lg">
                        {challengeStats.summary.totalProperimesDinars?.toLocaleString("fr-FR") || 0} {CURRENCY.symbol}
                      </StatNumber>
                    </Stat>
                    <Stat bg="purple.50" p={3} borderRadius="md">
                      <StatLabel color="purple.700">Ventes Extra</StatLabel>
                      <StatNumber color="purple.800" fontSize="lg">
                        {challengeStats.summary.totalExtraDinars?.toLocaleString("fr-FR") || 0} {CURRENCY.symbol}
                      </StatNumber>
                    </Stat>
                    <Stat bg="blue.50" p={3} borderRadius="md">
                      <StatLabel color="blue.700">Total Points</StatLabel>
                      <StatNumber color="blue.800" fontSize="lg">
                        {challengeStats.summary.totalPoints || 0}
                      </StatNumber>
                    </Stat>
                  </SimpleGrid>
                </Box>

                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                  <Stat>
                    <StatLabel>Transactions</StatLabel>
                    <StatNumber>{challengeStats.summary.totalTransactions || 0}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Articles vendus</StatLabel>
                    <StatNumber>{challengeStats.summary.totalItems || 0}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Profils actifs</StatLabel>
                    <StatNumber>{challengeStats.summary.participatingProfiles || 0}</StatNumber>
                  </Stat>
                </SimpleGrid>

                <Divider />

                <Box>
                  <Text fontWeight="bold" mb={3}>
                    Statistiques par profil
                  </Text>
                  {challengeStats.profileStats?.length > 0 ? (
                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Rang</Th>
                            <Th>Opérateur</Th>
                            <Th isNumeric>Points</Th>
                            <Th isNumeric>Ventes ({CURRENCY.symbol})</Th>
                            <Th isNumeric>Extra ({CURRENCY.symbol})</Th>
                            <Th isNumeric>Propérimés ({CURRENCY.symbol})</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {challengeStats.profileStats.map((profile) => (
                            <Tr key={profile.codeOperateur}>
                              <Td>
                                <Badge
                                  colorScheme={
                                    profile.rank === 1
                                      ? "yellow"
                                      : profile.rank === 2
                                        ? "gray"
                                        : profile.rank === 3
                                          ? "orange"
                                          : "blue"
                                  }
                                >
                                  #{profile.rank}
                                </Badge>
                              </Td>
                              <Td fontWeight="medium">{profile.nomOperateur}</Td>
                              <Td isNumeric fontWeight="bold" color="blue.600">
                                {profile.points}
                              </Td>
                              <Td isNumeric color="teal.600">
                                {profile.salesDinars?.toLocaleString("fr-FR") || 0}
                              </Td>
                              <Td isNumeric color="purple.600">
                                {profile.extraDinars?.toLocaleString("fr-FR") || 0}
                              </Td>
                              <Td isNumeric color="orange.600">
                                {profile.properimesDinars?.toLocaleString("fr-FR") || 0}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  ) : (
                    <Text color="gray.500" textAlign="center">
                      Aucune activité pendant ce challenge.
                    </Text>
                  )}
                </Box>
              </VStack>
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                Impossible de charger les statistiques.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onStatsModalClose}>
              Fermer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Evaluate Completion Condition Modal */}
      <Modal isOpen={isEvalModalOpen} onClose={onEvalModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Évaluation de la condition – {selectedChallenge?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isEvalLoading ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" color="teal.500" />
              </Flex>
            ) : evaluationResult?.error ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {evaluationResult.error}
              </Alert>
            ) : evaluationResult ? (
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Text fontWeight="semibold" fontSize="sm" color="gray.500">
                    Condition :
                  </Text>
                  <Badge
                    colorScheme={getConditionScheme(evaluationResult.completionCondition)}
                    fontSize="sm"
                  >
                    {getConditionLabel(evaluationResult.completionCondition)}
                  </Badge>
                </HStack>

                <Alert
                  status={evaluationResult.completed ? "success" : "info"}
                  borderRadius="md"
                >
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">
                      {evaluationResult.completed
                        ? "✅ Condition atteinte !"
                        : "⏳ Condition non encore atteinte"}
                    </Text>
                    <Text fontSize="sm">{evaluationResult.reason}</Text>
                  </Box>
                </Alert>

                {evaluationResult.completionCondition === "revenue_target" &&
                  evaluationResult.revenueTarget && (
                    <Box>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          Chiffre d'affaires actuel
                        </Text>
                        <Text fontSize="sm" fontWeight="bold" color="teal.600">
                          {evaluationResult.currentRevenue?.toLocaleString("fr-FR")} {CURRENCY.symbol} /{" "}
                            {evaluationResult.revenueTarget?.toLocaleString("fr-FR")} {CURRENCY.symbol}
                        </Text>
                      </Flex>
                      <Progress
                        w="100%"
                        bg={trackBg}
                        value={evaluationResult.progressPct || 0}
                        colorScheme={evaluationResult.completed ? "green" : "teal"}
                        size="md"
                        borderRadius="full"
                        hasStripe={!evaluationResult.completed}
                      />
                      <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                        {evaluationResult.progressPct || 0}%
                      </Text>
                    </Box>
                  )}

                {evaluationResult.completionCondition === "product_sales_target" &&
                  evaluationResult.productResults?.length > 0 && (
                    <Box>
                      <Text fontWeight="semibold" mb={2} fontSize="sm">
                        Progression par produit
                      </Text>
                      <VStack spacing={3} align="stretch">
                        {evaluationResult.productResults.map((p, idx) => (
                          <Box
                            key={idx}
                            border="1px solid"
                            borderColor={p.met ? "green.200" : borderColor}
                            borderRadius="md"
                            p={3}
                            bg={p.met ? "green.50" : undefined}
                          >
                            <Flex justify="space-between" mb={1}>
                              <Text fontSize="sm" fontWeight="medium">
                                {p.productName}
                              </Text>
                              <Badge colorScheme={p.met ? "green" : "orange"}>
                                {p.met ? "✓ Atteint" : "En cours"}
                              </Badge>
                            </Flex>
                            <Flex justify="space-between" mb={1}>
                              <Text fontSize="xs" color="gray.500">
                                Ventes : {p.currentSales} / {p.targetSales}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {p.progressPct}%
                              </Text>
                            </Flex>
                            <Progress
                              w="100%"
                              bg={trackBg}
                              value={p.progressPct}
                              colorScheme={p.met ? "green" : "orange"}
                              size="sm"
                              borderRadius="full"
                            />
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onEvalModalClose}>
              Fermer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Condition Met Overlay (global) */}
      <Modal isOpen={isConditionMetModalOpen} onClose={onConditionMetModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Condition atteinte – {selectedChallenge?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="success" borderRadius="md" mb={4}>
              <AlertIcon />
              La condition de clôture du challenge a été atteinte. Voulez-vous le terminer maintenant ?
            </Alert>

            {conditionMetEvaluation?.completionCondition === 'revenue_target' && (
              <Box mb={3}>
                <Text fontSize="sm">Chiffre d'affaires actuel : <strong>{conditionMetEvaluation.currentRevenue?.toLocaleString('fr-FR')} {CURRENCY.symbol}</strong></Text>
                <Text fontSize="sm">Objectif : <strong>{conditionMetEvaluation.revenueTarget?.toLocaleString('fr-FR')} {CURRENCY.symbol}</strong></Text>
              </Box>
            )}

            {conditionMetEvaluation?.completionCondition === 'product_sales_target' && (
              <Box>
                <Text fontSize="sm" mb={2}>Progression des produits :</Text>
                <VStack spacing={2} align="stretch">
                  {conditionMetEvaluation.productResults?.map((p, idx) => (
                    <Box key={idx} p={2} borderRadius="md" border="1px solid" borderColor={p.met ? 'green.200' : borderColor}>
                      <Flex justify="space-between">
                        <Text fontSize="sm">{p.productName}</Text>
                        <Badge colorScheme={p.met ? 'green' : 'orange'}>{p.met ? 'Atteint' : 'En cours'}</Badge>
                      </Flex>
                      <Text fontSize="xs" color="gray.500">{p.currentSales} / {p.targetSales} ({p.progressPct}%)</Text>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} w="100%">
              <Button
                colorScheme="green"
                onClick={() => { handleCompleteByCondition(selectedChallenge); onConditionMetModalClose(); }}
                w={{ base: '100%', sm: 'auto' }}
              >
                Terminer maintenant
              </Button>
              <Button
                variant="ghost"
                onClick={onConditionMetModalClose}
                w={{ base: '100%', sm: 'auto' }}
              >
                Continuer jusqu'à la date de fin
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}